/**
 * Course-progress percentage DISPLAY helpers (KAN-146 AC3).
 *
 * The bug: instructor and learner views rendered the SAME progress fraction with
 * DIFFERENT rounding (instructor "66.67%" vs learner "67%" for 2/3). The backend
 * now emits a single canonical 1-decimal-place percentage for every
 * course-progress value, so the frontend MUST render that value VERBATIM and the
 * one remaining client-computed path (the in-course timeline) MUST use the SAME
 * 1-dp rule.
 *
 * CANONICAL RULE = 1 decimal place, via `roundPercentage` (`Math.round(raw*10)/10`)
 * from the read-only shared helper `shared/utils/quiz-pass.utils.ts`. NEVER
 * re-round an already-canonical backend value — that is exactly the double-round
 * bug this util replaces.
 */

import { roundPercentage } from '@shared/utils/quiz-pass.utils';

/**
 * Render a backend-canonical course-progress percentage VERBATIM with a `%`
 * suffix. Does NOT round — the backend value is already canonical (1-dp). This
 * only centralizes the null/0 guard and the `%` suffix so every display site is
 * consistent.
 *
 * Examples: `null|undefined|0` -> "0%"; `66.7` -> "66.7%"; `100` -> "100%"
 * (never "100.0%").
 */
export function formatCourseProgressPercent(value: number | null | undefined): string {
  return `${value ?? 0}%`;
}

/**
 * Compute a course-progress percentage on the CLIENT (the in-course timeline,
 * which derives progress from in-memory navigation-item statuses rather than the
 * backend value) using the SAME canonical 1-dp rule as the backend, so the
 * timeline header agrees with every server-emitted surface.
 *
 * @returns the 1-dp percentage, or 0 when `total` is not positive.
 */
export function roundCourseProgress(completed: number, total: number): number {
  return total > 0 ? roundPercentage((completed / total) * 100) : 0;
}
